import { useEffect } from "react";
import { CalendarDialog } from "@/components/CalendarDialog";
import { MapPin, Users, Award, TrendingUp, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

export function ContactSection() {
  useEffect(() => {
    // Load GHL form embed script
    const script = document.createElement('script');
    script.src = 'https://link.msgsndr.com/js/form_embed.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://link.msgsndr.com/js/form_embed.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <section id="contact-section" className="section-padding bg-background-dark relative overflow-hidden" aria-labelledby="contact-heading">
      {/* Premium background */}
      <div className="absolute inset-0 grid-pattern-dark opacity-50" />
      <div className="absolute top-0 right-0 w-[400px] sm:w-[700px] h-[400px] sm:h-[700px] bg-primary/[0.06] rounded-full blur-[100px] sm:blur-[180px] translate-x-1/3 -translate-y-1/3" />
      <div className="absolute bottom-0 left-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-primary/[0.04] rounded-full blur-[80px] sm:blur-[120px] -translate-x-1/3 translate-y-1/3" />
      
      {/* Bottom separator gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      <div className="container-wide relative">
        <div className="max-w-6xl mx-auto">
          {/* Premium Final CTA Header */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-8 sm:mb-10 lg:mb-12"
          >
            <div className="section-badge-dark mb-4 sm:mb-5">
              <Sparkles className="w-3.5 sm:w-4 h-3.5 sm:h-4" aria-hidden="true" />
              <span className="text-xs sm:text-sm">Get Started Today</span>
            </div>
            <h2 id="contact-heading" className="heading-section text-foreground-dark mb-4 text-balance px-2 sm:px-0">
              Ready to Get More Leads for Your{" "}
              <span className="text-primary">Trade Business?</span>
            </h2>
            <p className="text-[0.9375rem] sm:text-base text-foreground-dark/70 max-w-2xl mx-auto px-4 sm:px-0 leading-[1.7]">
              Book a free strategy call with Australia's specialist digital marketing agency for trades and construction. We'll show you exactly how to get more leads, reviews and high-value jobs.
            </p>
          </motion.div>

          {/* CTA + Form Grid */}
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-start">
            {/* Left - Premium CTA */}
            <motion.div
              initial={{ opacity: 0, x: -32 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="text-center lg:text-left order-2 lg:order-1"
            >
              {/* Large animated CTA button */}
              <div className="mb-8 sm:mb-10">
                <CalendarDialog
                  buttonVariant="dark-primary"
                  buttonSize="lg"
                  buttonClassName="w-full sm:w-auto min-w-[280px] sm:min-w-[300px] h-14 sm:h-16 text-base sm:text-lg font-bold rounded-xl animate-pulse-soft shadow-[0_8px_32px_-8px_hsl(98_58%_38%/0.5)]"
                />
                <p className="text-foreground-dark/50 text-[0.8125rem] sm:text-[0.875rem] mt-3">Free strategy call • No obligation • Australia-wide</p>
              </div>

              {/* Trust Badges - Vertical stack */}
              <div className="flex flex-col items-start gap-4 w-full max-w-[220px] mx-auto lg:mx-0">
                {[
                  { icon: MapPin, text: "Australia-wide Service" },
                  { icon: Users, text: "Trade Marketing Experts" },
                  { icon: Award, text: "Proven Growth Systems" },
                  { icon: TrendingUp, text: "Measurable Results" },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 + index * 0.08, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-center gap-3 text-foreground-dark/70"
                  >
                    <div className="w-10 h-10 sm:w-9 sm:h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 sm:w-4 sm:h-4 text-primary" />
                    </div>
                    <span className="font-medium text-base sm:text-[0.9375rem] whitespace-nowrap">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* AEO-friendly summary */}
              <div className="mt-8 p-4 bg-foreground-dark/5 rounded-xl border border-foreground-dark/10">
                <h3 className="text-foreground-dark font-semibold text-[0.9375rem] sm:text-base mb-2">What you'll get on your free call:</h3>
                <ul className="text-foreground-dark/60 text-[0.8125rem] sm:text-[0.875rem] space-y-1.5">
                  <li>• Audit of your current digital presence</li>
                  <li>• Custom strategy for your trade business</li>
                  <li>• Clear roadmap to get more quality leads</li>
                  <li>• Pricing and timeline for your goals</li>
                </ul>
              </div>
            </motion.div>

            {/* Right - GHL Embedded Form */}
            <motion.div
              initial={{ opacity: 0, x: 32 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="bg-card rounded-2xl overflow-hidden shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-border/30 order-1 lg:order-2"
            >
              <iframe
                src="https://api.leadconnectorhq.com/widget/form/diRNWqSDomWX8M6X5NUu"
                style={{ width: '100%', height: '724px', border: 'none', borderRadius: '12px' }}
                id="inline-diRNWqSDomWX8M6X5NUu"
                data-layout="{'id':'INLINE'}"
                data-trigger-type="alwaysShow"
                data-trigger-value=""
                data-activation-type="alwaysActivated"
                data-activation-value=""
                data-deactivation-type="neverDeactivate"
                data-deactivation-value=""
                data-form-name="Tradie Marketing Enquiry Form"
                data-height="724"
                data-layout-iframe-id="inline-diRNWqSDomWX8M6X5NUu"
                data-form-id="diRNWqSDomWX8M6X5NUu"
                title="Tradie Marketing Enquiry Form - FullScale Marketing Australia"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
